/*
PURPOSE: Display content feed items with a consistent, appealing UI

USE CASES:
- Displaying music releases in the user dashboard
- Showing artist content updates in feed displays
- Presenting various content types with appropriate visual distinction
- Providing links to external streaming platforms

DOMAIN BOUNDARIES:
- Responsible for rendering content items with appropriate formatting
- Handles visual presentation only, not content data fetching
- Provides interactive elements for content navigation

ASSUMPTIONS:
- Content items follow the platform's ContentItem interface
- External links are provided in the relatedData.musicRelease.externalIds field
- Brand icons are available from the react-icons library
*/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentItem, CommentWithContentContext, MentionMetadata } from 'shared/types/contentTypes';
import { appleMusicAlbumUrl } from 'shared/utils/appleMusicUrl';
import { IconType } from 'react-icons';
import {
  FaSpotify,
  FaBandcamp,
  FaYoutube,
  FaSoundcloud,
  FaApple,
  FaDeezer
} from 'react-icons/fa';
import { SiTidal } from 'react-icons/si';
import { TbWorld } from 'react-icons/tb';
import { Music } from 'lucide-react';
import {
  CommentFeed
} from '../Comments';
import ContentCommentSection from '../ContentCommentSection/ContentCommentSection';
import {
  getComments,
  createComment,
  updateComment,
  deleteComment,
  reactToComment
} from '../../api/api';
import ArtistAvatar from '../ArtistAvatar/ArtistAvatar';
import { useAuth } from '../../hooks/useAuth';

interface ContentFeedItemProps {
  item: ContentItem;
  onClick?: (item: ContentItem) => void;
  forceCompactLayout?: boolean;
  forceShowComments?: boolean;
}

// Configuration for different content types
interface ContentTypeContext {
  firstComment: CommentWithContentContext | null;
  allComments: CommentWithContentContext[];
  isLoadingFirstComment: boolean;
}

interface ContentTypeConfig {
  badge: string | ((item: ContentItem, currentUserId?: string) => string);
  fetchInitialData?: boolean;
  initialDataType?: 'firstComment';
  actionsRenderer?: (item: ContentItem, context: ContentTypeContext, toggleComments?: (e: React.MouseEvent) => void, isExpanded?: boolean) => React.ReactNode;
  previewRenderer?: (item: ContentItem, context: ContentTypeContext, toggleComments?: (e: React.MouseEvent) => void, isExpanded?: boolean, toggleExpansion?: (e: React.MouseEvent) => void) => React.ReactNode;
  excludeCommentIds?: (context: ContentTypeContext) => string[];
}

// Common helper functions to eliminate duplication
const truncateToWords = (text: string, wordCount: number = 10) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= wordCount) return text;
  return words.slice(0, wordCount).join(' ') + '...';
};

const getEntityName = (item: ContentItem, contentType?: string) => {
  if (item.entityType === 'musicRelease') {
    return item.relatedData?.musicRelease?.title || 'this album';
  } else if (item.entityType === 'artist') {
    return item.relatedData?.artist?.name || 'this artist';
  } else if (item.entityType === 'musicTrack') {
    // For track discussions, use the enhanced title that includes album info
    if (contentType === 'discussion') {
      const trackTitle = item.relatedData?.musicTrack?.title;
      const albumTitle = item.relatedData?.musicRelease?.title;

      if (trackTitle && albumTitle) {
        return `${trackTitle} from ${albumTitle}`;
      }

      return trackTitle || 'this track';
    }
    return item.relatedData?.musicTrack?.title || 'this track';
  } else if (item.entityType === 'youtubeVideo') {
    const videoTitle = item.relatedData?.youtubeVideo?.title;
    const videoArtist = item.relatedData?.youtubeVideo?.artistName;
    if (videoTitle && videoArtist) {
      return `${videoTitle} by ${videoArtist}`;
    }
    return videoTitle || 'this video';
  }
  // Fallback to the original logic
  return item.relatedData?.artist?.name ||
    item.relatedData?.musicRelease?.title ||
    item.relatedData?.musicTrack?.title ||
    item.relatedData?.youtubeVideo?.title ||
    'this';
};

const getEntityInfo = (item: ContentItem) => {
  const artistId = item.relatedData?.artist?.guid ||
    item.relatedData?.musicRelease?.artistId ||
    item.relatedData?.musicTrack?.artistId ||
    item.relatedData?.youtubeVideo?.artistId;

  if (item.entityType === 'musicRelease') {
    return {
      type: 'album' as const,
      artistId,
      albumId: item.relatedData?.musicRelease?.releaseId
    };
  } else if (item.entityType === 'artist') {
    return {
      type: 'artist' as const,
      artistId
    };
  } else if (item.entityType === 'musicTrack') {
    return {
      type: 'track' as const,
      artistId,
      albumId: item.relatedData?.musicRelease?.releaseId,
      trackId: item.relatedData?.musicTrack?.trackId
    };
  } else if (item.entityType === 'youtubeVideo') {
    return {
      type: 'video' as const,
      artistId,
      videoId: item.relatedData?.youtubeVideo?.videoId,
      youtubeVideoId: item.relatedData?.youtubeVideo?.youtubeVideoId
    };
  }

  return {
    type: 'artist' as const,
    artistId
  };
};

// Shared preview renderer for discussion-style content
const renderDiscussionStylePreview = (
  item: ContentItem,
  context: ContentTypeContext,
  toggleComments?: (e: React.MouseEvent) => void,
  isExpanded?: boolean,
  toggleExpansion?: (e: React.MouseEvent) => void,
  renderClickableEntityName?: (name: string, entityInfo?: {
    type: 'artist' | 'album' | 'track' | 'video';
    artistId?: string;
    albumId?: string;
    trackId?: string;
    videoId?: string;
    youtubeVideoId?: string;
  }) => React.ReactNode,
  styleConfig?: {
    borderClass: string;
    bgClass?: string;
    prefixText: string;
    isDancefloorShare?: boolean;
  },
  navigate?: ReturnType<typeof useNavigate>
) => {
  if (context.isLoadingFirstComment) {
    return (
      <div className="animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }
  
  const entityName = getEntityName(item, item.contentType);
  const entityInfo = getEntityInfo(item);
  
  const defaultStyle = {
    borderClass: 'border-l border-coral-purple/30',
    bgClass: '',
    prefixText: 'Contribution on'
  };

  const style = { ...defaultStyle, ...styleConfig };

  // Check for embedded first comment (from backend enrichment) first
  if (item.firstComment) {
    const displayText = isExpanded ? item.firstComment.text : truncateToWords(item.firstComment.text);
    const needsTruncation = item.firstComment.text.trim().split(/\s+/).length > 10;

    return (
      <div className="discussion-preview">
        <blockquote
          className={`${style.borderClass} pl-2 py-1 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200 rounded-r-lg ${style.bgClass}`}
          onClick={needsTruncation ? toggleExpansion : toggleComments}
          title={needsTruncation ? (isExpanded ? "Click to show excerpt" : "Click to read full text") : "Click to view full discussion"}
        >
          <p className="text-gray-800 text-base sm:text-lg leading-relaxed font-light italic">
            {item.contentType === 'discussion' ? `"${displayText}"` : displayText}
          </p>
        </blockquote>
        <div className="text-sm font-light text-gray-600 mt-3 pl-4">
          <span className="text-gray-500">— </span>
          {(() => {
            // For dancefloor shares, show "Shared by [sender]"
            if (style.isDancefloorShare && item.firstComment?.displayName) {
              return (
                <span className="text-gray-500">
                  Shared by <span className="text-coral-orange font-medium">{item.firstComment.displayName}</span>
                </span>
              );
            }

            // Extract the last word (preposition) from prefixText if it has one
            const parts = style.prefixText.trim().replace(/:$/, '').split(' ');
            const lastWord = parts[parts.length - 1];
            const isPreposition = ['on', 'of', 'for', 'to'].includes(lastWord.toLowerCase());
            const baseText = isPreposition ? parts.slice(0, -1).join(' ') : style.prefixText.trim().replace(/:$/, '');
            const preposition = isPreposition ? lastWord : 'on';

            return (
              <>
                <span className="text-gray-500">{baseText}</span>
                {item.sourceAlignedUserId && item.sourceAlignedUserDisplayName && navigate && (
                  <>
                    <span className="text-gray-500"> by </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/u/${item.sourceAlignedUserId}`);
                      }}
                      className="text-coral-orange hover:underline"
                    >
                      {item.sourceAlignedUserDisplayName}
                    </button>
                  </>
                )}
                <span className="text-gray-500"> {preposition} </span>
                {renderClickableEntityName?.(entityName, entityInfo)}
              </>
            );
          })()}
        </div>
      </div>
    );
  }
  
  // Fallback to context comments (for backward compatibility with existing loaded comments)
  if (context.allComments.length > 0) {
    // If single comment, show full text with editorial style
    if (context.allComments.length === 1) {
      const comment = context.firstComment!;
      const displayText = isExpanded ? comment.text : truncateToWords(comment.text);
      const needsTruncation = comment.text.trim().split(/\s+/).length > 10;
      
      return (
        <div className="discussion-preview">
          <blockquote
            className={`${style.borderClass} pl-2 py-1 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200 rounded-r-lg ${style.bgClass}`}
            onClick={needsTruncation ? toggleExpansion : toggleComments}
            title={needsTruncation ? (isExpanded ? "Click to show excerpt" : "Click to read full text") : "Click to view full discussion"}
          >
            <p className="text-gray-800 text-base sm:text-lg leading-relaxed font-light italic">
              {item.contentType === 'discussion' ? `"${displayText}"` : displayText}
            </p>
          </blockquote>
          <div className="text-sm font-light text-gray-600 mt-3 pl-4">
            <span className="text-gray-500">— </span>
            {(() => {
              // For dancefloor shares, show "Shared by [sender]"
              if (style.isDancefloorShare && context.firstComment?.displayName) {
                return (
                  <span className="text-gray-500">
                    Shared by <span className="text-coral-orange font-medium">{context.firstComment.displayName}</span>
                  </span>
                );
              }

              // Extract the last word (preposition) from prefixText if it has one
              const parts = style.prefixText.trim().replace(/:$/, '').split(' ');
              const lastWord = parts[parts.length - 1];
              const isPreposition = ['on', 'of', 'for', 'to'].includes(lastWord.toLowerCase());
              const baseText = isPreposition ? parts.slice(0, -1).join(' ') : style.prefixText.trim().replace(/:$/, '');
              const preposition = isPreposition ? lastWord : 'on';

              return (
                <>
                  <span className="text-gray-500">{baseText}</span>
                  {item.sourceAlignedUserId && item.sourceAlignedUserDisplayName && navigate && (
                    <>
                      <span className="text-gray-500"> by </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/u/${item.sourceAlignedUserId}`);
                        }}
                        className="text-coral-orange hover:underline"
                      >
                        {item.sourceAlignedUserDisplayName}
                      </button>
                    </>
                  )}
                  <span className="text-gray-500"> {preposition} </span>
                  {renderClickableEntityName?.(entityName, entityInfo)}
                </>
              );
            })()}
          </div>
        </div>
      );
    }

    // Multiple comments - show as editorial quotes (only for discussions)
    if (item.contentType === 'discussion') {
      return (
        <div className="discussion-preview space-y-3">
          {context.allComments.slice(0, 2).map((comment) => (
            <blockquote
              key={comment.commentId}
              className={`${style.borderClass} pl-2 py-1 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200 rounded-r-lg ${style.bgClass}`}
              onClick={toggleComments}
              title="Click to view full discussion"
            >
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed font-light italic">
                &ldquo;{truncateToWords(comment.text)}&rdquo;
              </p>
            </blockquote>
          ))}
          <div className="text-sm font-light text-gray-600 pl-4">
            <span className="text-gray-500">— From a conversation about </span>
            {renderClickableEntityName?.(entityName, entityInfo)}
          </div>
        </div>
      );
    }
  }
  
  // Fallback for content with no comments
  return (
    <div className="pl-6 border-l-4 border-gray-200">
      <p className="text-gray-500 text-lg italic font-light">
        {item.contentType === 'discussion' ? 'Start the conversation' : 
         item.contentType === 'review' ? 'Review coming soon' : 
         'Recommendation coming soon'}
      </p>
      <div className="text-sm text-gray-500 mt-2">
        <span>— About </span>
        {renderClickableEntityName?.(entityName, entityInfo)}
      </div>
    </div>
  );
};

const getContentTypeConfig = (
  renderClickableEntityName: (name: string, entityInfo?: {
    type: 'artist' | 'album' | 'track' | 'video';
    artistId?: string;
    albumId?: string;
    trackId?: string;
    videoId?: string;
    youtubeVideoId?: string;
  }) => React.ReactNode,
  navigate?: ReturnType<typeof useNavigate>
): Record<string, ContentTypeConfig> => ({
  discussion: {
    badge: (item) => {
      // Check for dancefloor share first
      const targetUser = item.relatedData?.targetUser;
      if (targetUser?.displayName) {
        return `${targetUser.displayName}'s Dancefloor`;
      }
      // For discussions, the badge should reflect what type of discussion it is
      if (item.entityType === 'musicRelease') {
        return 'Album Discussion';
      } else if (item.entityType === 'artist') {
        return 'Artist Discussion';
      } else if (item.entityType === 'musicTrack') {
        return 'Track Discussion';
      } else if (item.entityType === 'youtubeVideo') {
        return 'Video Share';
      }
      return 'Discussion';
    },
    actionsRenderer: (item, _context, toggleComments, isExpanded) => (
      isExpanded ? null : (
        <button
          onClick={toggleComments}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-coral-pink transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          {item.commentStats?.totalCount !== undefined && item.commentStats.totalCount > 0 && (
            <span className="text-xs font-medium leading-none">
              {item.commentStats.totalCount}
            </span>
          )}
          <span className="text-sm font-extralight leading-none">
            {(item.commentStats?.totalCount ?? 0) > 0 ? 'Join conversation' : 'Start a conversation'}
          </span>
        </button>
      )
    ),
    previewRenderer: (item, context, toggleComments, isExpanded, toggleExpansion) => {
      const isDancefloorShare = !!item.relatedData?.targetUser;
      return renderDiscussionStylePreview(
        item,
        context,
        toggleComments,
        isExpanded,
        toggleExpansion,
        renderClickableEntityName,
        {
          borderClass: 'border-l border-coral-purple/30',
          prefixText: 'Contribution on',
          isDancefloorShare
        },
        navigate
      );
    },
    excludeCommentIds: () => {
      // Don't exclude any comments - show all in expanded view
      return [];
    }
  },

  review: {
    badge: (item) => {
      if (item.entityType === 'musicRelease') {
        return 'Album Review';
      } else if (item.entityType === 'artist') {
        return 'Artist Review';
      } else if (item.entityType === 'musicTrack') {
        return 'Track Review';
      } else if (item.entityType === 'youtubeVideo') {
        return 'Video Review';
      }
      return 'Review';
    },
    actionsRenderer: (item, _context, toggleComments, isExpanded) => (
      isExpanded ? null : (
        <button
          onClick={toggleComments}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-coral-pink transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          {item.commentStats?.totalCount !== undefined && item.commentStats.totalCount > 0 && (
            <span className="text-xs font-medium leading-none">
              {item.commentStats.totalCount}
            </span>
          )}
          <span className="text-sm font-extralight leading-none">
            Join discussion
          </span>
        </button>
      )
    ),
    previewRenderer: (item, context, toggleComments, isExpanded, toggleExpansion) => {
      return renderDiscussionStylePreview(
        item,
        context,
        toggleComments,
        isExpanded,
        toggleExpansion,
        renderClickableEntityName,
        {
          borderClass: 'border-l-2 border-coral-purple/30',
          prefixText: 'Review of'
        },
        navigate
      );
    },
    excludeCommentIds: () => {
      // Don't exclude any comments - show all in expanded view
      return [];
    }
  },
  
  listeningRoom: {
    badge: (item) => {
      if (item.entityType === 'musicRelease') {
        return 'Listening Room Pick';
      } else if (item.entityType === 'artist') {
        return 'Artist Recommendation';
      } else if (item.entityType === 'musicTrack') {
        return 'Track Pick';
      } else if (item.entityType === 'youtubeVideo') {
        return 'Video Share';
      }
      return 'Listening Room';
    },
    actionsRenderer: (item, _context, toggleComments, isExpanded) => (
      isExpanded ? null : (
        <button
          onClick={toggleComments}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-coral-pink transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          {item.commentStats?.totalCount !== undefined && item.commentStats.totalCount > 0 && (
            <span className="text-xs font-medium leading-none">
              {item.commentStats.totalCount}
            </span>
          )}
          <span className="text-sm font-extralight leading-none">
            Join discussion
          </span>
        </button>
      )
    ),
    previewRenderer: (item, context, toggleComments, isExpanded, toggleExpansion) => {
      return renderDiscussionStylePreview(
        item,
        context,
        toggleComments,
        isExpanded,
        toggleExpansion,
        renderClickableEntityName,
        {
          borderClass: 'border-l-2 border-coral-purple/30',
          bgClass: 'bg-coral-purple/5',
          prefixText: 'Added to the Listening Room:'
        },
        navigate
      );
    },
    excludeCommentIds: () => {
      // Don't exclude any comments - show all in expanded view
      return [];
    }
  },

  post: {
    badge: 'Personal Update',
    actionsRenderer: (item, _context, toggleComments, isExpanded) => (
      isExpanded ? null : (
        <button
          onClick={toggleComments}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-coral-pink transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          {item.commentStats?.totalCount !== undefined && item.commentStats.totalCount > 0 && (
            <span className="text-xs font-medium leading-none">
              {item.commentStats.totalCount}
            </span>
          )}
          <span className="text-sm font-extralight leading-none">
            {(item.commentStats?.totalCount ?? 0) > 0 ? 'Join conversation' : 'Start a conversation'}
          </span>
        </button>
      )
    ),
    previewRenderer: (item, context, toggleComments, isExpanded, toggleExpansion) => {
      // Personal posts use the discussion-style preview showing first comment
      return renderDiscussionStylePreview(
        item,
        context,
        toggleComments,
        isExpanded,
        toggleExpansion,
        renderClickableEntityName,
        {
          borderClass: 'border-l border-coral-purple/30',
          prefixText: 'Personal update'
        },
        navigate
      );
    },
    excludeCommentIds: () => {
      // Don't exclude any comments - show all in expanded view
      return [];
    }
  },

  // Default configuration for other content types
  default: {
    badge: (item) => {
      // Existing logic from getContentTypeLabel()
      if (item.relatedData?.musicRelease) {
        if (item.relatedData.musicRelease.type) {
          const releaseType = item.relatedData.musicRelease.type;
          switch (releaseType) {
            case 'album':
              return 'New Album';
            case 'single':
              return 'New Single';
            case 'compilation':
              return 'New Compilation';
            case 'ep':
              return 'New EP';
            default:
              return `New ${String(releaseType).charAt(0).toUpperCase() + String(releaseType).slice(1)}`;
          }
        }
        return 'New Release';
      }
      
      switch (item.contentType) {
        case 'newRelease':
          return 'New Release';
        case 'post':
          return 'Post';
        case 'signal':
          return 'Signal';
        case 'event':
          return 'Event';
        default:
          if (item.relatedData?.artist) {
            return 'Artist Update';
          }
          return 'Update';
      }
    }
  }
});

const ContentFeedItem: React.FC<ContentFeedItemProps> = ({ item, onClick, forceCompactLayout = false, forceShowComments = false }) => {
  const navigate = useNavigate();
  const { currentUser2: currentUser } = useAuth();

  // Helper to detect dancefloor music shares
  const getDancefloorShareContext = () => {
    const targetUser = item.relatedData?.targetUser;
    if (!targetUser?.displayName) return null;

    // Check if this is the current user (shared with "you")
    const isCurrentUserTarget = currentUser?.userId === targetUser.userId;

    return {
      recipientName: isCurrentUserTarget ? 'you' : targetUser.displayName,
      recipientId: targetUser.userId,
      isCurrentUserTarget
    };
  };

  const dancefloorShareContext = getDancefloorShareContext();

  // State for expanded comments section
  const [showComments, setShowComments] = useState(forceShowComments);
  const [commentCount, setCommentCount] = useState(item.commentStats?.totalCount || 0);
  const [comments, setComments] = useState<CommentWithContentContext[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>(undefined);

  // New state for content type specific data
  const [firstComment, setFirstComment] = useState<CommentWithContentContext | null>(null);
  const [allComments, setAllComments] = useState<CommentWithContentContext[]>([]);
  const [isLoadingFirstComment, setIsLoadingFirstComment] = useState(false);

  // State for expanded excerpt text
  const [isExcerptExpanded, setIsExcerptExpanded] = useState(false);

  const limit = 5; // Number of comments to load at a time

  // Helper function to generate enhanced content title
  const getEnhancedContentTitle = () => {
    const trackTitle = item.relatedData?.musicTrack?.title;
    const albumTitle = item.relatedData?.musicRelease?.title;

    // For track comments, show "Track Name from Album Name"
    if (trackTitle && albumTitle) {
      return `${trackTitle} from ${albumTitle}`;
    }

    // Fallback to individual titles or generic fallback
    return trackTitle || albumTitle || item.title || 'Post';
  };

  // Helper function to determine content source label
  const getContentSourceLabel = () => {
    if (item.contentSource === 'backed_artist') return 'Backed Artist';
    if (item.contentSource === 'aligned_artist') return 'Aligned Artist';
    if (item.contentSource === 'aligned_user') return 'Aligned Contributor';
    if (item.contentSource === 'user_activity') return null; // No label for user activity
    
    // Backward compatibility: if no contentSource but has sourceAlignedUserId, it's aligned content
    if (item.sourceAlignedUserId) return 'Aligned Contributor';
    
    return null; // No label if we can't determine source
  };
  
  // Determine if we have media to display
  // Check for standard mediaInfo or YouTube video thumbnail
  const youtubeVideoThumbnail = item.relatedData?.youtubeVideo?.thumbnailUrl;
  const hasMedia = !!item.mediaInfo?.thumbnailUrl || !!youtubeVideoThumbnail;
  const mediaThumbnailUrl = item.mediaInfo?.thumbnailUrl || youtubeVideoThumbnail;
  
  // Unified function to render clickable entity names
  const renderClickableEntityName = (name: string, entityInfo?: {
    type: 'artist' | 'album' | 'track' | 'video';
    artistId?: string;
    albumId?: string;
    trackId?: string;
    videoId?: string;
    youtubeVideoId?: string;
  }) => {
    // Handle click events with proper navigation
    const handleEntityClick = (e: React.MouseEvent, navType: string, artistId?: string, albumId?: string, trackId?: string, videoId?: string) => {
      e.stopPropagation(); // Prevent card click

      if (!artistId) return;

      let url = `/artist/${artistId}`;

      if (navType === 'video' && videoId) {
        // Navigate to Coral video page instead of YouTube
        url += `?view=discography&video=${videoId}`;
      } else if (navType === 'album' && albumId) {
        url += `?view=discography&album=${albumId}`;
      } else if (navType === 'track' && albumId && trackId) {
        url += `?view=discography&album=${albumId}&track=${trackId}`;
      } else if (navType === 'artist') {
        url += `?view=discography`;
      }

      window.location.href = url;
    };
    
    // Check if name has the pattern "Track Name from Album Name"
    const fromMatch = name.match(/^(.+)\s+from\s+(.+)$/);
    
    if (fromMatch) {
      const [, trackName, albumName] = fromMatch;
      return (
        <>
          <button
            onClick={(e) => handleEntityClick(e, 'track', entityInfo?.artistId, entityInfo?.albumId, entityInfo?.trackId)}
            className="text-coral-pink hover:text-coral-orange transition-colors duration-200 hover:underline font-medium"
            title={`View ${trackName} in Works`}
          >
            {trackName}
          </button>
          <span className="text-gray-500"> from </span>
          <button
            onClick={(e) => handleEntityClick(e, 'album', entityInfo?.artistId, entityInfo?.albumId)}
            className="text-coral-pink hover:text-coral-orange transition-colors duration-200 hover:underline font-medium"
            title={`View ${albumName} in Works`}
          >
            {albumName}
          </button>
        </>
      );
    }

    // No "from" pattern, make the whole name clickable based on entity type
    if (entityInfo && entityInfo.artistId) {
      const titleText = entityInfo.type === 'video' ? `View ${name} in Works` : `View ${name} in Works`;
      return (
        <button
          onClick={(e) => handleEntityClick(e, entityInfo.type, entityInfo.artistId, entityInfo.albumId, entityInfo.trackId, entityInfo.videoId)}
          className="text-coral-pink hover:text-coral-orange transition-colors duration-200 hover:underline font-medium"
          title={titleText}
        >
          {name}
        </button>
      );
    }

    // Fallback for cases without navigation data
    return <span className="text-coral-pink">{name}</span>;
  };
  
  // Get content type configuration with access to renderClickableEntityName and navigate
  const CONTENT_TYPE_CONFIG = getContentTypeConfig(renderClickableEntityName, navigate);
  const contentConfig = CONTENT_TYPE_CONFIG[item.contentType || 'default'] || CONTENT_TYPE_CONFIG.default;
  
  // Create context object for renderers
  const contentContext: ContentTypeContext = {
    firstComment,
    allComments,
    isLoadingFirstComment
  };
  
  // Extract supported artist info from relatedData with priority based on content type and entity type
  const getArtistInfoForDisplay = () => {
    // For discussions, prioritize based on what the discussion is about
    if (item.contentType === 'discussion') {
      if (item.entityType === 'musicRelease') {
        // Album discussion - show album's artist, but could still link to artist
        return {
          name: item.relatedData?.musicRelease?.artistName || item.relatedData?.artist?.name,
          id: item.relatedData?.musicRelease?.artistId || item.relatedData?.artist?.guid,
          imageUrl: item.relatedData?.artist?.imageUrl // Always use artist image for avatar
        };
      } else if (item.entityType === 'artist') {
        // Artist discussion - show artist info
        return {
          name: item.relatedData?.artist?.name,
          id: item.relatedData?.artist?.guid || (item.entityType === 'artist' ? item.entityId : undefined),
          imageUrl: item.relatedData?.artist?.imageUrl
        };
      } else if (item.entityType === 'musicTrack') {
        // Track discussion - show track's artist
        return {
          name: item.relatedData?.musicTrack?.artistName || item.relatedData?.artist?.name,
          id: item.relatedData?.musicTrack?.artistId || item.relatedData?.artist?.guid,
          imageUrl: item.relatedData?.artist?.imageUrl
        };
      } else if (item.entityType === 'youtubeVideo') {
        // YouTube video discussion - show video's artist
        return {
          name: item.relatedData?.youtubeVideo?.artistName || item.relatedData?.artist?.name,
          id: item.relatedData?.youtubeVideo?.artistId || item.relatedData?.artist?.guid,
          imageUrl: item.relatedData?.artist?.imageUrl
        };
      }
    }

    // Default priority for non-discussion content or fallback
    return {
      name: item.relatedData?.artist?.name ||
        item.relatedData?.musicRelease?.artistName ||
        item.relatedData?.musicTrack?.artistName ||
        item.relatedData?.youtubeVideo?.artistName,
      id: item.relatedData?.artist?.guid ||
        item.relatedData?.musicRelease?.artistId ||
        item.relatedData?.musicTrack?.artistId ||
        item.relatedData?.youtubeVideo?.artistId ||
        (item.entityType === 'artist' ? item.entityId : undefined),
      imageUrl: item.relatedData?.artist?.imageUrl
    };
  };
  
  const artistInfo = getArtistInfoForDisplay();
  const artistName = artistInfo.name;
  const artistId = artistInfo.id;
  
  // Get external streaming links if this has music release data
  const streamingLinks = React.useMemo(() => {
    if (!item.relatedData?.musicRelease?.externalIds) {
      return [];
    }
    
    const links = [];
    const externalIds = item.relatedData.musicRelease.externalIds;
    
    // Add Spotify link if available
    if (externalIds.spotify) {
      const spotifyId = externalIds.spotify.includes('spotify.com') 
        ? externalIds.spotify.split('/').pop()?.split('?')[0] 
        : externalIds.spotify;
      
      links.push({
        type: 'spotify',
        url: `spotify:album:${spotifyId}`,
        fallbackUrl: `https://open.spotify.com/album/${spotifyId}`,
        label: 'Spotify'
      });
    }
    
    // Add Bandcamp link if available
    if (externalIds.bandcamp) {
      links.push({
        type: 'bandcamp',
        url: externalIds.bandcamp,
        label: 'Bandcamp'
      });
    }
    
    // Add Apple Music link if available
    const appleUrl = appleMusicAlbumUrl(externalIds.appleMusic);
    if (appleUrl) {
      links.push({
        type: 'appleMusic',
        url: appleUrl,
        label: 'Apple Music'
      });
    }
    
    // Add SoundCloud link if available
    if (externalIds.soundcloud) {
      links.push({
        type: 'soundcloud',
        url: externalIds.soundcloud,
        label: 'SoundCloud'
      });
    }
    
    // Add YouTube link if available
    if (externalIds.youtube) {
      links.push({
        type: 'youtube',
        url: externalIds.youtube,
        label: 'YouTube'
      });
    }
    
    // Add Deezer link if available
    if (externalIds.deezer) {
      links.push({
        type: 'deezer',
        url: externalIds.deezer,
        label: 'Deezer'
      });
    }
    
    // Add Tidal link if available
    if (externalIds.tidal) {
      links.push({
        type: 'tidal',
        url: externalIds.tidal,
        label: 'Tidal'
      });
    }
    
    return links;
  }, [item]);
  
  // Determine content type label/badge
  const getContentTypeLabel = () => {
    const badge = contentConfig.badge;
    return typeof badge === 'function' ? badge(item, currentUser?.userId) : badge;
  };
  
  // Handle navigation - for track discussions go to track page, otherwise go to album page
  const handleAlbumNavigation = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    // For YouTube videos, navigate based on video type
    if (item.entityType === 'youtubeVideo' && artistId) {
      const video = item.relatedData?.youtubeVideo;

      // Music videos with trackId → navigate to track page (consolidate discussion there)
      if (video?.trackId && video?.releaseId) {
        window.location.href = `/artist/${artistId}?view=discography&album=${video.releaseId}&track=${video.trackId}`;
        return;
      }

      // Other videos (live performances, no trackId) → navigate to video page
      if (video?.videoId) {
        window.location.href = `/artist/${artistId}?view=discography&video=${video.videoId}`;
        return;
      }
    }

    // For track discussions, navigate to the track page
    if (item.entityType === 'musicTrack' && artistId && item.relatedData?.musicRelease?.releaseId && item.relatedData?.musicTrack?.trackId) {
      window.location.href = `/artist/${artistId}?view=discography&album=${item.relatedData.musicRelease.releaseId}&track=${item.relatedData.musicTrack.trackId}`;
    }
    // For album discussions or other content, navigate to album page
    else if (artistId && item.relatedData?.musicRelease?.releaseId) {
      window.location.href = `/artist/${artistId}?view=discography&album=${item.relatedData.musicRelease.releaseId}`;
    } else if (artistId) {
      // If no specific album, just go to Works tab
      window.location.href = `/artist/${artistId}?view=discography`;
    }
  };
  
  // Fetch first comment for discussion content types
  const fetchFirstComment = async () => {
    if (!item.contentId || !contentConfig.fetchInitialData) return;

    try {
      setIsLoadingFirstComment(true);

      // Fetch up to 3 comments to show snippets
      const response = await getComments({
        contentId: item.contentId,
        limit: 3,
        entityType: item.entityType,
        entityId: item.entityId
      });

      if (response.comments.length > 0) {
        // Convert to CommentWithContentContext
        const commentsWithContext = response.comments.map(comment => ({
          ...comment,
          contentContext: {
            contentId: item.contentId!,
            contentType: item.contentType || 'post',
            contentTitle: getEnhancedContentTitle()
          }
        })) as CommentWithContentContext[];

        setFirstComment(commentsWithContext[0]);
        setAllComments(commentsWithContext);
      }
    } catch (error) {
      console.error('Error fetching first comment:', error);
    } finally {
      setIsLoadingFirstComment(false);
    }
  };
  
  // Load first comment on mount for specific content types
  useEffect(() => {
    if (contentConfig.fetchInitialData && contentConfig.initialDataType === 'firstComment') {
      fetchFirstComment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.contentId, item.contentType]);
  
  // Map of platform types to their corresponding icons and colors
  const platformIconMap: Record<string, { Icon: IconType; color: string }> = {
    spotify: { Icon: FaSpotify, color: 'text-[#1DB954]' },
    bandcamp: { Icon: FaBandcamp, color: 'text-[#1DA0C3]' },
    applemusic: { Icon: FaApple, color: 'text-[#FB233B]' },
    soundcloud: { Icon: FaSoundcloud, color: 'text-[#FF7700]' },
    youtube: { Icon: FaYoutube, color: 'text-[#FF0000]' },
    deezer: { Icon: FaDeezer, color: 'text-[#00C7F2]' },
    tidal: { Icon: SiTidal, color: 'text-[#000000]' },
  };
  
  // Helper function to handle Spotify URI with fallback
  const handleSpotifyClick = (e: React.MouseEvent, spotifyUri: string, fallbackUrl: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent card click
    
    // Use window.location to try opening Spotify URI
    // If it fails, the browser will ignore it and we can fall back
    const startTime = Date.now();
    
    // Listen for blur event to detect if app opened
    let appOpened = false;
    
    const onBlur = () => {
      appOpened = true;
      window.removeEventListener('blur', onBlur);
    };
    
    const onFocus = () => {
      // If we regain focus quickly, the app likely didn't open
      const timeElapsed = Date.now() - startTime;
      if (timeElapsed < 1000 && !appOpened) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
    
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    
    // Try to open the Spotify URI
    window.location.href = spotifyUri;
    
    // Fallback timeout in case no blur/focus events fire
    setTimeout(() => {
      if (!appOpened) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    }, 1500);
  };

  // Function to render platform icon
  const renderPlatformIcon = (type: string) => {
    const lowercaseType = type.toLowerCase();
    const iconData = platformIconMap[lowercaseType] || { Icon: TbWorld, color: 'text-gray-600' };
    
    // Fix TypeScript error by using a proper type assertion
    const IconComponent = iconData.Icon as React.ComponentType<React.SVGAttributes<SVGElement>>;
    return (
      <IconComponent 
        width={14} 
        height={14} // Using standard SVG properties instead of size
        className={`${iconData.color} opacity-65`} // Add opacity for visual cohesion
      />
    );
  };
  
  const handleClick = () => {
    if (onClick) onClick(item);
  };
  
  // Handle link click without triggering the card click
  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  
  // Fetch comments when the comments section is expanded
  const fetchComments = async (reset: boolean = false) => {
    if (!item.contentId) return;

    try {
      setIsLoadingComments(true);

      const response = await getComments({
        contentId: item.contentId,
        limit,
        lastEvaluatedKey: reset ? undefined : lastEvaluatedKey,
        entityType: item.entityType,
        entityId: item.entityId
      });

      // Convert CommentRecord[] to CommentWithContentContext[]
      const contextualComments = response.comments.map(comment => ({
        ...comment,
        contentContext: {
          contentId: item.contentId!,
          contentType: item.contentType || 'post',
          contentTitle: getEnhancedContentTitle()
        }
      })) as CommentWithContentContext[];

      if (reset) {
        setComments(contextualComments);
      } else {
        setComments(prev => [...prev, ...contextualComments]);
      }

      setLastEvaluatedKey(response.lastEvaluatedKey);

      if (response.totalCount !== undefined) {
        setCommentCount(response.totalCount);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };
  
  // Load comments when comments section is first expanded
  useEffect(() => {
    if (showComments && comments.length === 0) {
      fetchComments(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComments]);
  
  // Toggle comments visibility
  const toggleComments = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    e.preventDefault(); // Prevent any default action
    
    // Update state after a brief delay to prevent any state conflicts
    setTimeout(() => {
      setShowComments(prevState => !prevState);
    }, 10);
  };
  
  // Toggle excerpt expansion
  const toggleExcerptExpansion = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    e.preventDefault(); // Prevent any default action
    setIsExcerptExpanded(prev => !prev);
  };
  
  // We don't need handleLoadMore anymore since we're using a simpler approach
  
  // Handle adding a new comment
  const handleAddComment = async (text: string, _contentId?: string, mentions?: MentionMetadata[]): Promise<void> => {
    if (!item.contentId) return;

    try {
      const newComment = await createComment({
        contentId: item.contentId,
        text,
        entityType: item.entityType,
        entityId: item.entityId,
        mentions
      });
      
      // Convert to CommentWithContentContext
      const commentWithContext = {
        ...newComment,
        contentContext: {
          contentId: item.contentId,
          contentType: item.contentType || 'post',
          contentTitle: getEnhancedContentTitle()
        }
      } as CommentWithContentContext;
      
      // Add the new comment to the state
      setComments(prev => [commentWithContext, ...prev]);
      setCommentCount(prev => prev + 1);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  
  // Handle adding a reply to a comment
  const handleReply = async (commentId: string, _contentId: string, text: string, mentions?: MentionMetadata[]): Promise<void> => {
    if (!item.contentId) return;

    try {
      // Note: We use item.contentId instead of contentId parameter because in ContentFeedCard
      // all comments are for the same content item
      const newReply = await createComment({
        contentId: item.contentId,
        text,
        parentCommentId: commentId,
        entityType: item.entityType,
        entityId: item.entityId,
        mentions
      });
      
      // Create reply with context to add to local state
      const replyWithContext: CommentWithContentContext = {
        ...newReply,
        contentContext: {
          contentId: item.contentId,
          contentType: item.contentType || 'post',
          contentTitle: getEnhancedContentTitle()
        }
      };
      
      // Add the new reply to the comments state
      setComments(prevComments => [
        ...prevComments,
        replyWithContext
      ]);
      
      // Also increment comment count
      setCommentCount(prev => prev + 1);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };
  
  // Handle liking a comment
  const handleLike = async (commentId: string, isLiked: boolean): Promise<void> => {
    if (!item.contentId) return;
    
    try {
      // The isLiked parameter from ContextualComment indicates the NEW state (after toggling)
      // So we use 'add' when isLiked is true, and 'remove' when isLiked is false
      await reactToComment({
        contentId: item.contentId,
        commentId,
        reactionType: 'resonate',
        action: isLiked ? 'add' : 'remove'
      });
      
      // Update will be handled by the ContextualCommentThread component
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };
  
  // Handle editing a comment
  const handleEdit = async (commentId: string, text: string): Promise<void> => {
    if (!item.contentId) return;
    
    try {
      await updateComment({
        contentId: item.contentId,
        commentId,
        text
      });
      
      // Update the comments state
      setComments(prev => 
        prev.map(comment => 
          comment.commentId === commentId 
            ? { ...comment, text } 
            : comment
        )
      );
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };
  
  // Handle deleting a comment
  const handleDelete = async (commentId: string): Promise<void> => {
    if (!item.contentId) return;
    
    try {
      await deleteComment({
        contentId: item.contentId,
        commentId
      });
      
      // Update the comment in the UI to show as deleted
      setComments(prev => 
        prev.map(comment => 
          comment.commentId === commentId 
            ? { ...comment, isDeleted: true, text: "[This comment has been deleted]" } 
            : comment
        )
      );
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };
  

  return (
    <div
      className="bg-white border-gray-100/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border backdrop-blur-sm hover:border-coral-pink/20 hover:-translate-y-1 mb-4 lg:mb-6 max-w-2xl mx-auto"
      onClick={handleClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={forceCompactLayout ? "p-4" : "p-6 lg:p-8"}>
        {/* Top section with artist name, content type badge, and streaming links */}
        <div className="mb-4">
          {/* Use column layout only on very small screens or when forced compact */}
          <div className={`flex ${forceCompactLayout ? 'flex-col' : 'flex-col min-[480px]:flex-row min-[480px]:justify-between min-[480px]:items-start'} gap-3`}>
            {/* Artist with avatar at top left */}
            {artistName && (
              <ArtistAvatar
                artistName={artistName}
                artistId={typeof artistId === 'string' ? artistId : undefined}
                imageUrl={artistInfo.imageUrl}
                size="sm"
                showName={true}
                showGenre={false}
                clickable={!!artistId}
              />
            )}

            {/* Content type badge and streaming links - shrink to fit when needed */}
            <div className="flex flex-nowrap items-center gap-2 min-[480px]:flex-shrink-0">
              {getContentTypeLabel() && (
                dancefloorShareContext ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/u/${dancefloorShareContext.recipientId}?view=gestures`);
                    }}
                    className="inline-block bg-gradient-to-r from-coral-pink/10 to-coral-orange/10 text-coral-pink text-[10px] min-[480px]:text-[11px] font-semibold px-2 min-[480px]:px-3 py-1 min-[480px]:py-1.5 rounded-full border border-coral-orange/20 flex-shrink-0 hover:from-coral-pink/20 hover:to-coral-orange/20 transition-colors"
                  >
                    {getContentTypeLabel()}
                  </button>
                ) : (
                  <span className="inline-block bg-gradient-to-r from-coral-pink/10 to-coral-orange/10 text-coral-pink text-[10px] min-[480px]:text-[11px] font-semibold px-2 min-[480px]:px-3 py-1 min-[480px]:py-1.5 rounded-full border border-coral-orange/20 flex-shrink-0">
                    {getContentTypeLabel()}
                  </span>
                )
              )}

              {/* Streaming platform links - keep inline with badge */}
              {streamingLinks.length > 0 && (
                <div className="flex flex-nowrap gap-1">
                  {streamingLinks.map((link, index) => (
                    <a
                      key={`${link.type}-${index}`}
                      href={link.type === 'spotify' ? '#' : link.url}
                      onClick={link.type === 'spotify' && link.fallbackUrl ?
                        (e) => handleSpotifyClick(e, link.url, link.fallbackUrl) :
                        handleLinkClick
                      }
                      target={link.type === 'spotify' ? undefined : "_blank"}
                      rel={link.type === 'spotify' ? undefined : "noopener noreferrer"}
                      className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 hover:bg-gray-100 transition-all hover:shadow-md hover:scale-110 duration-200"
                      title={`Listen on ${link.label}`}
                    >
                      {renderPlatformIcon(link.type)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>


        {/* Main content area - responsive layout */}
        <div className={`${hasMedia ? (forceCompactLayout ? 'flex flex-col gap-4' : 'lg:flex lg:gap-5') : ''}`}>
          {/* YouTube Video Embed - shown for YouTube video content */}
          {item.entityType === 'youtubeVideo' && item.relatedData?.youtubeVideo?.youtubeVideoId && (
            <div className={`flex-shrink-0 mb-4 ${forceCompactLayout ? '' : 'lg:mb-0'}`}>
              <div
                className={`rounded-2xl overflow-hidden shadow-lg ${
                  forceCompactLayout
                    ? 'w-full'
                    : 'w-full lg:w-80'
                }`}
              >
                <iframe
                  key={item.relatedData.youtubeVideo.youtubeVideoId}
                  width="100%"
                  height={forceCompactLayout ? '200' : '180'}
                  src={`https://www.youtube-nocookie.com/embed/${item.relatedData.youtubeVideo.youtubeVideoId}`}
                  title={item.relatedData.youtubeVideo.title || 'YouTube video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{ border: 0 }}
                />
              </div>
            </div>
          )}

          {/* Enhanced Thumbnail - only shown for non-YouTube content with media */}
          {hasMedia && item.entityType !== 'youtubeVideo' && (
            <div className={`flex-shrink-0 mb-4 ${forceCompactLayout ? '' : 'lg:mb-0'}`}>
              <div
                className={`group relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  forceCompactLayout
                    ? 'w-full h-48'
                    : 'w-full lg:w-36 h-48 lg:h-36'
                }`}
                style={{
                  backgroundImage: `url(${mediaThumbnailUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                onClick={handleAlbumNavigation}
                title={
                  item.relatedData?.musicRelease?.title
                    ? `View ${item.relatedData.musicRelease.title} in Works`
                    : 'View in Works'
                }
              >
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />

                {/* Play indicator */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                    <Music className="w-6 h-6 text-coral-pink" />
                  </div>
                </div>

                {/* Subtle gradient overlay for better text contrast */}
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
            </div>
          )}
          
          {/* Content area - title and description together on wider screens */}
          <div className={`${hasMedia ? 'lg:flex-1' : 'w-full'}`}>
            {/* Title - with better typography */}
            {item.title && (
              <h3
                className={`font-bold leading-tight tracking-tight cursor-pointer hover:text-coral-pink transition-colors duration-200 ${
                  item.contentType === 'discussion' ? 'text-2xl lg:text-3xl mb-3' : 'text-xl lg:text-2xl mb-2'
                }`}
                onClick={handleAlbumNavigation}
                title={
                  item.entityType === 'youtubeVideo'
                    ? `View ${item.relatedData?.youtubeVideo?.title || 'video'} in Works`
                    : item.relatedData?.musicRelease?.title
                      ? `View ${item.relatedData.musicRelease.title} in Works`
                      : 'View in Works'
                }
              >
                {item.title}
              </h3>
            )}
            
            {/* Description - inline with title on wider screens when media present */}
            <div className={`${!hasMedia || !item.title ? 'mt-3' : ''}`}>
              {contentConfig.previewRenderer ? (
                contentConfig.previewRenderer(item, contentContext, toggleComments, isExcerptExpanded, toggleExcerptExpansion)
              ) : (
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed font-light">
                  {item.text}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar - consolidated design with pill tag and conversation toggle on same line */}
        <div className="mt-6 pt-4 border-t border-gray-100/50">
          {contentConfig.actionsRenderer ? (
            <div className="flex items-center justify-between">
              {/* actionsRenderer returns null when expanded, collapse arrow shows instead */}
              {contentConfig.actionsRenderer(item, contentContext, toggleComments, showComments) || (
                <button
                  onClick={toggleComments}
                  className="text-gray-400 hover:text-coral-pink transition-colors duration-200"
                  aria-label="Close conversation"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="18 15 12 9 6 15"></polyline>
                  </svg>
                </button>
              )}

              {artistName && (
                <div className="flex items-center">
                  {getContentSourceLabel() && (
                    <>
                      {artistId ? (
                        <a 
                          href={`/artist/${artistId}`}
                          onClick={(e) => e.stopPropagation()} // Prevent card click
                          className="bg-gradient-to-r from-coral-pink/5 to-coral-orange/5 text-coral-orange text-xs px-4 py-1.5 rounded-full font-medium border border-coral-orange/10 hover:from-coral-pink/10 hover:to-coral-orange/10 transition-all duration-200 artist-badge-link"
                          data-testid="artist-badge-link"
                          data-artist-name={artistName}
                        >
                          {getContentSourceLabel()}
                        </a>
                      ) : (
                        <span className="bg-gradient-to-r from-coral-pink/5 to-coral-orange/5 text-coral-orange text-xs px-4 py-1.5 rounded-full font-medium border border-coral-orange/10">
                          {getContentSourceLabel()}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              {/* When collapsed: show "Join/Start conversation" button */}
              {!showComments ? (
                <button
                  onClick={toggleComments}
                  className="inline-flex items-center gap-1.5 text-gray-500 hover:text-coral-pink transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  {commentCount > 0 && (
                    <span className="text-xs font-medium leading-none">
                      {commentCount}
                    </span>
                  )}
                  <span className="text-sm font-extralight leading-none">
                    {commentCount > 0 ? 'Join the conversation' : 'Start a conversation'}
                  </span>
                </button>
              ) : (
                /* When expanded: show collapse arrow on the right */
                <button
                  onClick={toggleComments}
                  className="text-gray-400 hover:text-coral-pink transition-colors duration-200"
                  aria-label="Close conversation"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="18 15 12 9 6 15"></polyline>
                  </svg>
                </button>
              )}
              
              {artistName && (
                <div className="flex items-center">
                  {getContentSourceLabel() && (
                    <>
                      {artistId ? (
                        <a 
                          href={`/artist/${artistId}`}
                          onClick={(e) => e.stopPropagation()} // Prevent card click
                          className="bg-gradient-to-r from-coral-pink/5 to-coral-orange/5 text-coral-orange text-xs px-4 py-1.5 rounded-full font-medium border border-coral-orange/10 hover:from-coral-pink/10 hover:to-coral-orange/10 transition-all duration-200 artist-badge-link"
                          data-testid="artist-badge-link"
                          data-artist-name={artistName}
                        >
                          {getContentSourceLabel()}
                        </a>
                      ) : (
                        <span className="bg-gradient-to-r from-coral-pink/5 to-coral-orange/5 text-coral-orange text-xs px-4 py-1.5 rounded-full font-medium border border-coral-orange/10">
                          {getContentSourceLabel()}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Expandable comments section */}
        {showComments && (
          <div className="pt-4 animate-fadeIn">
            {/* Dancefloor shares use single-thread mode like GestureCard */}
            {dancefloorShareContext && item.contentId ? (
              <ContentCommentSection
                contentId={item.contentId}
                contentType={item.contentType || 'discussion'}
                contentTitle={item.title || 'Dancefloor share'}
                singleThreadMode={true}
                responseMode={true}
                textFirstLayout={true}
                showAuthorAttribution={true}
                compact={true}
                placeholder="Add your response..."
              />
            ) : (
              <CommentFeed
                contentItems={[{
                  contentItem: {
                    id: item.contentId,
                    type: item.contentType,
                    title: item.title || ''
                  },
                  comments: (() => {
                    // Filter out excluded comment IDs if configured
                    const excludeIds = contentConfig.excludeCommentIds?.(contentContext) || [];
                    return comments.filter(comment => !excludeIds.includes(comment.commentId));
                  })()
                }]}
                isLoading={isLoadingComments}
                onAddComment={handleAddComment}
                onReply={handleReply}
                onLike={handleLike}
                onDelete={handleDelete}
                onEdit={handleEdit}
                className="shadow-none px-0 py-0 bg-transparent rounded-none"
                compact={true}
                pageContext="contentCard"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Export with the name ContentFeedCard for consistency with file name
export default ContentFeedItem;
export { ContentFeedItem as ContentFeedCard };